import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import i18next from 'i18next';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import U2000Tab from '../u2000Core/components/tab/U2000Tab';
import { doInitLU, luHandleApiError, type U3147InitLU } from './U3147Service';
import { U3147ContextProvider } from './U3147Context';
import { U2000_ORG_DISPLAY_TIMEZONE } from '../u2000Core/tools/U2000DateTimeHelpers';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U3147EmployeeNotConfigured from './components/U3147EmployeeNotConfigured';
import U3147TodayView from './components/today/U3147TodayView';
import U3147HistoryView from './components/history/U3147HistoryView';

export const NS = 'u3147';

export const U3147ROLES = {
  READ: 'U3147:READ',
  TRACK: 'U3147:TRACK',
  EXPORT: 'U3147:EXPORT',
  EDIT: 'U3147:EDIT',
  DELETE: 'U3147:DELETE',
  OTHER_EMPLOYEE: 'U3147:OTHEREMPLOYEE',
} as const;

export type U3147Role = (typeof U3147ROLES)[keyof typeof U3147ROLES];

const U3147_TAB_TODAY = 'today';
const U3147_TAB_HISTORY = 'history';

function u3147TabFromPathname(pathname: string): string {
  return /\/U3147\/history\/?$/.test(pathname) ? U3147_TAB_HISTORY : U3147_TAB_TODAY;
}

const defaultInitLU: U3147InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  roundingMinutes: 1,
  organizationTimezone: U2000_ORG_DISPLAY_TIMEZONE,
  employee: null,
  openSessionId: null,
  personalization: { admIncludeSubProject: null },
};

const U3147: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3147InitLU>(defaultInitLU);
  const [activeTab, setActiveTab] = useState<string>(() => u3147TabFromPathname(location.pathname));

  useEffect(() => {
    setActiveTab(u3147TabFromPathname(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    i18next.loadNamespaces([NS], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3147InitLU>({
    doInitLUFn: async () => await doInitLU(u2000AxiosAuthenticated),
    onSuccess: (payload: U3147InitLU) => {
      setInitLUData(payload);
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUData(defaultInitLU);
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init LU au montage
  }, [t]);

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t('title', { ns: NS });
    setLogicalUnitTitle(title);
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(tabId === U3147_TAB_HISTORY ? '/U3147/history' : '/U3147');
  };

  const tabLabels = useMemo(
    () => ({
      today: t('tabToday', { ns: NS, defaultValue: "Aujourd'hui" }),
      history: t('tabHistory', { ns: NS, defaultValue: 'Historique' }),
    }),
    [t],
  );

  const todayView = useMemo(() => <U3147TodayView />, []);
  const historyView = useMemo(() => <U3147HistoryView />, []);

  const tabs = useMemo(
    () => [
      { id: U3147_TAB_TODAY, label: tabLabels.today, content: todayView },
      { id: U3147_TAB_HISTORY, label: tabLabels.history, content: historyView },
    ],
    [tabLabels, todayView, historyView],
  );

  if (!i18nReady || !initLUReady) return null;

  if (initLUData.employee == null) {
    return <U3147EmployeeNotConfigured />;
  }

  return (
    <U3147ContextProvider initLU={initLUData}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4">
        <U2000Tab
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          fillRemainingHeight
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        />
      </div>
    </U3147ContextProvider>
  );
};

export default U3147;
